import moment from 'moment';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons';
import { FunctionComponent, SetStateAction } from 'react';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { EditUploadLineGoodEmissionDialog } from '../../../dialogs/settings/data-import/edit-upload-line-good-emission-dialog';
import { IEditFileUploadLineGoodEmission, IFileUploadLineGoodEmission } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { useNavigate } from 'react-router-dom';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { Chip, Tooltip, IconButton, Stack } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';

export const CsvUploadGoodEmissions: FunctionComponent<{
  uploadId: string;
  fileUploadLineGoodEmissions: IFileUploadLineGoodEmission[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    uploadLine: IFileUploadLineGoodEmission) => void,
  deleteHandler: (id: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLineGoodEmission | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineGoodEmission) => void,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  status: string,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  selectAllFiltered?: boolean,
  onExitSelectAllFiltered?: (remainingIdsOnPage: string[]) => void,
}> = ({
  uploadId,
  fileUploadLineGoodEmissions,
  editClickHandler,
  openDialog,
  setValidatedUploadLines,
  selected,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  setSelected,
  editUploadLine,
  setOrder,
  setOrderBy,
  closeDialogHandler,
  submitHandler,
  deleteHandler,
  status,
  isSubmitting,
  selectAllFiltered,
  onExitSelectAllFiltered }) => {

    const navigate = useNavigate()
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = Array.isArray(uploadLine.validationErrors) ? uploadLine.validationErrors : []

        const fieldList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''

        const errorList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''

        const severityList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : status === 'pending' ? (
          <Chip
            color={uploadLine.commitStatus === 'FAILED' ? 'error' : 'success'}
            label={uploadLine.commitStatus === 'FAILED' ? 'Failed' : 'Ready'}
            size="small"
            variant="outlined"
          />
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const headCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...((status !== 'discarded' && ((status === 'completed') || ((status === 'pending' || status === 'validated') && !isReportPeriodLocked))) ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'capitalGoodName', numeric: false, label: 'Product', sortable: true },
        { id: 'unitsConsumed', numeric: false, label: 'Number of Units Purchased', sortable: true },
        { id: 'startDate', numeric: false, label: 'Date Purchased', sortable: true },
      ];

    const rows = status === 'pending'
      ? mapValidationErrors(fileUploadLineGoodEmissions || [])
      : fileUploadLineGoodEmissions?.map((uploadLine: IFileUploadLineGoodEmission) => {
        return {
          id: uploadLine.id,
          capitalGoodName: uploadLine.capitalGoodName,
          startDate: moment(uploadLine.startDate).format('DD/MM/YYYY'),
          unitsConsumed: uploadLine.unitsConsumed,
          action: status === 'validated' && !isReportPeriodLocked ? (
            <TableActionButtons
              id={uploadLine.id}
              includeEdit={true}
              includeDelete={true}
              onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
              onDelete={(id: string) => deleteHandler(id)}
            />
          ) : status === 'completed' ? (
            <TableActionButtons
              id={uploadLine.id}
              includeView={true}
              onView={() => uploadLine.emissionId ? viewTransactionClickHandler(uploadLine.emissionId) : undefined}
              disableButton={!uploadLine.emissionId}
            />
          ) : null
        }
      })

    const viewTransactionClickHandler = (transactionId: string) => {
      navigate(`/emissions/overview/${transactionId}?backLink=${encodeURIComponent(`/settings/data-upload/import-history/emissions/${uploadId}?tabId=1`)}&backText=${encodeURIComponent('Back to import')}`)
    }

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => {
        return {
          id: row
        }
      })
      setValidatedUploadLines(formatted)
    }

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    return (
      <>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          onOrderChange={handleOrderChange}
          selectByIcon={(status === 'validated' || status === 'pending') && !isReportPeriodLocked ? false : true}
          selectByCheckboxOnly={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          selectAllFiltered={selectAllFiltered}
          onExitSelectAllFiltered={onExitSelectAllFiltered}
          inputPage={inputPage}
          setInputPage={setInputPage}
          haveCheckboxSelect={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
        {editUploadLine && openDialog && !isReportPeriodLocked &&
          <EditUploadLineGoodEmissionDialog
            open={openDialog}
            isSubmitting={isSubmitting}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine} />}
      </>
    )
  }